import React, { useCallback, useMemo } from "react";

import I24CheckboxOn from "assets/icons/I24CheckboxOn";
import I24CheckboxOff from "assets/icons/I24CheckboxOff";
import I24CheckboxMinus from "assets/icons/I24CheckboxMinus";

import styleConst from "constants/style";

import {
  CheckboxLeadContainer,
  CheckboxLabel,
  Container,
  Header,
  Options,
  Row,
  Title,
} from "./styles";

type Option = {
  label: string;
  value: string;
};

type Props = {
  value: Array<Option>;
  options: Array<Option>;
  selected?: Array<Option>;
  onSelectOption: (options: Array<Option>) => void;
  title: string;
};

type CheckboxProps = {
  selected?: boolean;
  option: Option;
  onSelect: (option: Option) => void;
};

type CheckboxLeadProps = {
  allSelected: boolean;
  someSelected: boolean;
  onClick: () => void;
};

const Checkbox = ({ option, selected, onSelect }: CheckboxProps) => {
  const onClick = () => {
    onSelect?.(option);
  };

  return (
    <Row onClick={onClick}>
      {selected ? (
        <I24CheckboxOn color={styleConst.colors.white} />
      ) : (
        <I24CheckboxOff color={styleConst.colors.black10} />
      )}
      <CheckboxLabel>{option.label}</CheckboxLabel>
    </Row>
  );
};

const CheckboxLead = ({
  allSelected,
  someSelected,
  onClick,
}: CheckboxLeadProps) => {
  const Icon = allSelected
    ? I24CheckboxOn
    : someSelected
    ? I24CheckboxMinus
    : I24CheckboxOff;
  const color = allSelected
    ? styleConst.colors.white
    : someSelected
    ? styleConst.colors.white
    : styleConst.colors.black10;

  return (
    <CheckboxLeadContainer onClick={onClick}>
      <Icon color={color} />
    </CheckboxLeadContainer>
  );
};

const CheckboxGroup: React.FC<Props> = ({
  value = [],
  options,
  onSelectOption,
  title,
}) => {
  const mappedOptions = useMemo(
    () => options.map(({ value: v }) => v),
    [options]
  );

  const mappedValues = useMemo(
    () => value.map(({ value: v }) => v),
    [value]
  );

  const isSomeOptionsSelected = useMemo(
    () =>
      !!mappedOptions.length &&
      mappedOptions.some(option => mappedValues.includes(option)),
    [mappedOptions, mappedValues]
  );

  const isEveryOptionsSelected = useMemo(
    () =>
      !!mappedOptions.length &&
      mappedOptions.every(option => mappedValues.includes(option)),
    [mappedOptions, mappedValues]
  );

  const isOptionSelected = useCallback(
    (option: Option) => {
      return value.map(({ value: v }) => v).includes(option?.value);
    },
    [value]
  );

  const onSelect = useCallback(
    (option: Option) => {
      const current = value || [];
      const selectedValues = current.map(({ value: v }) => v);
      if (selectedValues.includes(option?.value)) {
        onSelectOption?.(
          current.filter(item => item.value !== option.value)
        );
      } else {
        onSelectOption?.([...current, option]);
      }
    },
    [value, onSelectOption]
  );

  const onLeadSelect = useCallback(() => {
    const nextOptions = isEveryOptionsSelected ? [] : options;
    onSelectOption?.(nextOptions);
  }, [isEveryOptionsSelected, options, onSelectOption]);

  return (
    <Container>
      <Header>
        <CheckboxLead
          allSelected={isEveryOptionsSelected}
          someSelected={isSomeOptionsSelected}
          onClick={onLeadSelect}
        />
        <Title>{title}</Title>
      </Header>
      <Options>
        {options.map(option => (
          <Checkbox
            key={option.value}
            option={option}
            onSelect={onSelect}
            selected={isOptionSelected(option)}
          />
        ))}
      </Options>
    </Container>
  );
};

export default CheckboxGroup;
